import { useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || '';

export default function TermsPage() {
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    document.title = 'Terms & Conditions | VE Goodies Vending Services DFW';

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Terms and Conditions for VE Goodies vending machine services in Carrollton and Dallas-Fort Worth. Learn about our service agreements, client responsibilities, and business terms.');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.href = `${SITE_URL}/terms`;

    // WebPage Schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-terms';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Terms & Conditions – VE Goodies",
      "description": "Terms and Conditions for VE Goodies vending machine services in Carrollton and Dallas-Fort Worth.",
      "url": `${SITE_URL}/terms`,
      "inLanguage": "en-US",
      "isPartOf": {
        "@type": "WebSite",
        "name": "VE Goodies",
        "url": SITE_URL
      },
      "publisher": {
        "@type": "Organization",
        "name": "VE Goodies",
        "telephone": "+14699223413",
        "email": "vegoodiesllc@gmail.com",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Carrollton",
          "addressRegion": "TX",
          "addressCountry": "US"
        }
      },
      "dateModified": today
    });
    document.head.appendChild(script);

    // BreadcrumbList Schema
    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.id = 'schema-breadcrumb';
    breadcrumbScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": SITE_URL
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Terms & Conditions",
          "item": `${SITE_URL}/terms`
        }
      ]
    });
    document.head.appendChild(breadcrumbScript);

    return () => {
      document.getElementById('schema-terms')?.remove();
      document.getElementById('schema-breadcrumb')?.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="pt-32 pb-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-6">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Terms &amp; Conditions
          </h1>
          <p className="text-lg text-gray-600 mb-12">
            Last Updated: January 2025
          </p>

          <div className="space-y-8 text-gray-700 leading-relaxed">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Agreement to Terms</h2>
              <p className="mb-4">
                By accessing and using VE Goodies' services, you agree to be bound by these Terms and Conditions. If you do not agree with any part of these terms, you may not use our services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Services Provided</h2>
              <p className="mb-4">
                VE Goodies provides vending machine placement, stocking, maintenance, and related services to businesses in the Dallas-Fort Worth metroplex. Our services include:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Free vending machine installation and placement</li>
                <li>Regular product stocking and inventory management</li>
                <li>24/7 maintenance and technical support</li>
                <li>Custom product selection and healthy options</li>
                <li>Custom branding solutions</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Client Responsibilities</h2>
              <p className="mb-4">
                Clients agree to:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Provide adequate space and electrical access for vending machine placement</li>
                <li>Maintain a safe environment for equipment operation</li>
                <li>Notify VE Goodies promptly of any equipment issues or concerns</li>
                <li>Allow reasonable access for restocking and maintenance</li>
                <li>Not tamper with or modify equipment without authorization</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Equipment Ownership</h2>
              <p className="mb-4">
                All vending machines and equipment remain the property of VE Goodies. Clients are responsible for the reasonable care and security of equipment placed on their premises.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Service Agreement Duration</h2>
              <p className="mb-4">
                Service agreements are typically established for a minimum term as specified in individual contracts. Either party may terminate the agreement with 30 days written notice, subject to contract terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Pricing and Payment</h2>
              <p className="mb-4">
                VE Goodies operates on a commission-based model. There are no upfront costs for machine placement or installation. Product pricing is set by VE Goodies and may be adjusted periodically.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Liability and Insurance</h2>
              <p className="mb-4">
                VE Goodies maintains appropriate insurance coverage for equipment and operations. Clients are responsible for maintaining their own premises liability insurance. VE Goodies is not liable for personal injury or property damage resulting from misuse of equipment.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Product Quality and Safety</h2>
              <p className="mb-4">
                We are committed to providing fresh, quality products. All products are sourced from reputable suppliers and handled according to food safety standards. Clients should report any product quality concerns immediately.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Privacy and Data</h2>
              <p className="mb-4">
                VE Goodies collects and uses client information in accordance with our Privacy Policy. We respect your privacy and protect your data according to applicable laws.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Modifications to Terms</h2>
              <p className="mb-4">
                VE Goodies reserves the right to modify these terms at any time. Clients will be notified of significant changes. Continued use of services after modifications constitutes acceptance of updated terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Dispute Resolution</h2>
              <p className="mb-4">
                Any disputes arising from these terms or our services will be resolved through good faith negotiation. If resolution cannot be reached, disputes will be handled according to Texas state law.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contact Information</h2>
              <p className="mb-4">
                For questions about these Terms &amp; Conditions, please contact us:
              </p>
              <div className="bg-gray-50 rounded-lg p-6 space-y-2">
                <p><strong>Email:</strong> vegoodiesllc@gmail.com</p>
                <p><strong>Phone:</strong> (469) 922-3413</p>
                <p><strong>Service Area:</strong> Dallas-Fort Worth Metroplex, TX</p>
              </div>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}